/**
 * Utility functions for object manipulation
 */

/**
 * Recursively removes undefined values from an object
 * 
 * Firestore does not accept undefined values, so this utility
 * filters them out before passing data to Firestore operations.
 * 
 * @param obj - The object to clean
 * @returns A new object with undefined values removed
 * 
 * @example
 * ```typescript
 * const data = { name: 'John', age: undefined, email: 'john@example.com' };
 * const cleaned = removeUndefinedFields(data);
 * // Result: { name: 'John', email: 'john@example.com' }
 * ```
 */
export function removeUndefinedFields<T extends Record<string, unknown>>(
  obj: T
): Partial<T> {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedFields(item as Record<string, unknown>)) as unknown as Partial<T>;
  }

  if (typeof obj !== 'object') {
    return obj as Partial<T>;
  }

  // Preserve Date objects as-is
  if (obj instanceof Date) {
    return obj as unknown as Partial<T>;
  }

  // Preserve Firestore Timestamp objects (duck-typing check for toDate method)
  if (typeof (obj as unknown as { toDate: unknown }).toDate === 'function' && typeof (obj as unknown as { seconds: unknown }).seconds === 'number') {
    return obj as unknown as Partial<T>;
  }

  const cleaned: Partial<T> = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      // Skip undefined values
      if (value === undefined) {
        continue;
      }

      // Preserve null values
      if (value === null) {
        cleaned[key] = value as T[Extract<keyof T, string>];
        continue;
      }

      // Preserve Date objects
      if (value instanceof Date) {
        cleaned[key] = value as T[Extract<keyof T, string>];
        continue;
      }

      // Preserve Firestore Timestamp objects (duck-typing check)
      if (typeof value === 'object' && value !== null && typeof (value as Record<string, unknown>).toDate === 'function' && typeof (value as Record<string, unknown>).seconds === 'number') {
        cleaned[key] = value as T[Extract<keyof T, string>];
        continue;
      }

      // Recursively clean nested objects
      if (typeof value === 'object' && !Array.isArray(value)) {
        const cleanedValue = removeUndefinedFields(value as Record<string, unknown>);
        // Only include if the cleaned object has properties
        if (Object.keys(cleanedValue).length > 0) {
          cleaned[key] = cleanedValue as T[Extract<keyof T, string>];
        }
      } else if (Array.isArray(value)) {
        // Clean arrays recursively
        cleaned[key] = value.map((item: unknown) =>
          typeof item === 'object' && item !== null
            ? removeUndefinedFields(item as Record<string, unknown>)
            : item
        ) as unknown as T[Extract<keyof T, string>];
      } else {
        // Include non-undefined primitive values
        cleaned[key] = value as T[Extract<keyof T, string>];
      }
    }
  }

  return cleaned;
}

/**
 * Deep equality check for objects and arrays
 * Handles nested structures and Dates correctly
 */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) {
    return false;
  }

  if (Array.isArray(a)) {
    if (!Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  if (Array.isArray(b)) return false;

  const recordA = a as Record<string, unknown>;
  const recordB = b as Record<string, unknown>;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(recordB, key)) return false;
    if (!deepEqual(recordA[key], recordB[key])) return false;
  }

  return true;
}
